// Home alert card: nudges the user to record any drug allergies BEFORE
// they add a medicine, so the cross-reactivity checker has something to
// match against. Round-4 #1 surface — 8 of 20 personas didn't realize
// the new allergy check existed because there was no way to find it.
//
// Render rules:
//   - Hidden until the prior first-run prompts are done (textSizeChosen
//     + profileKindChosen).
//   - Hidden once the user has at least one allergy on file.
//   - Auto-fetches once on mount; uses the existing /api/allergies surface.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAllergiesApi } from '../api/allergies';
import { useMe } from '../hooks/useMe';
import { useSubjectName } from '../hooks/useSubjectName';

export function AllergyAlertCard(): JSX.Element | null {
  const { me } = useMe();
  const subject = useSubjectName();
  const api = useAllergiesApi();
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!me) return;
    if (me.accessibility?.textSizeChosen !== true) return;
    if (me.accessibility?.profileKindChosen !== true) return;
    let cancelled = false;
    void api.list().then(
      (items) => {
        if (!cancelled) setCount(items.length);
      },
      () => {
        // best-effort; don't render the nudge on failure
        if (!cancelled) setCount(-1);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [me, api]);

  if (!me) return null;
  if (count === null || count < 0 || count > 0) return null;
  // Defer same as HomeTour: don't stack on top of the first-run prompts.
  if (me.accessibility?.textSizeChosen !== true) return null;
  if (me.accessibility?.profileKindChosen !== true) return null;

  const whose = subject.possessive ? `${subject.possessive}` : 'your';

  return (
    <section
      aria-label="Allergy reminder"
      style={{
        margin: '0 0 var(--space-md)',
        padding: 'var(--space-md)',
        background: '#fef3c7',
        border: '1px solid #fbbf24',
        borderRadius: 'var(--radius-md)',
        display: 'grid',
        gap: 10,
      }}
    >
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#78350f' }}>
        Tell us about {whose} allergies before adding the first medicine.
      </h3>
      <p style={{ margin: 0, fontSize: 16, lineHeight: 1.5, color: '#78350f' }}>
        We will warn you the moment a new prescription would cross-react with something on your
        allergy list. Penicillin, sulfa, NSAIDs, ACE inhibitors, statins, and codeine all have
        common cross-reactions.
      </p>
      <div>
        <Link
          to="/allergies"
          className="home-hero__cta"
          style={{ display: 'inline-block', textDecoration: 'none' }}
        >
          Add allergies
        </Link>
      </div>
    </section>
  );
}
